import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  VStack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import moment from "moment";

const DateSelector = ({ onDateSelect }) => {
  const [selectedOption, setSelectedOption] = useState("All Time");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const predefinedOptions = [
    { label: "Today", value: "today" },
    { label: "Last 7 days", value: "last7Days" },
    { label: "Last 30 days", value: "last30Days" },
    { label: "This week", value: "thisWeek" },
    { label: "This month", value: "thisMonth" },
    { label: "This year", value: "thisYear" },
    { label: "Last 3 Months", value: "lastThreeMonths" },
    { label: "All Time", value: "allTime" },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    // Add event listener to detect clicks outside
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOptionClick = (option) => {
    setSelectedOption(option.label);
    setIsDropdownOpen(false);

    // Define date ranges based on selection
    const selectedRange = getPredefinedDateRange(option.value);
    // Pass selected range back to parent
    onDateSelect(selectedRange);
  };

  const getPredefinedDateRange = (value) => {
    const now = moment();

    switch (value) {
      case "today":
        return {
          startDate: now.startOf("day").toDate(),
          endDate: now.endOf("day").toDate(),
        };
      case "last7Days":
        return {
          startDate: now.clone().subtract(6, "days").startOf("day").toDate(), // Including today
          endDate: now.clone().endOf("day").toDate(),
        };
      case "last30Days":
        return {
          startDate: now.clone().subtract(29, "days").startOf("day").toDate(),
          endDate: now.clone().endOf("day").toDate(),
        };
      case "thisWeek":
        return {
          startDate: now.clone().startOf("isoWeek").toDate(), // ISO week starts on Monday
          endDate: now.clone().endOf("isoWeek").toDate(),
        };
      case "thisMonth":
        return {
          startDate: now.clone().startOf("month").toDate(),
          endDate: now.clone().endOf("month").toDate(),
        };
      case "thisYear":
        return {
          startDate: now.clone().startOf("year").toDate(),
          endDate: now.clone().endOf("year").toDate(),
        };
      case "lastThreeMonths":
        return {
          startDate: now.clone().subtract(2, "months").startOf("month").toDate(),
          endDate: now.clone().endOf("month").toDate(),
        };
      case "allTime":
        return {
          startDate: null, // You can set this to a very early date if needed, e.g., new Date(0)
          endDate: null,   // Or leave it as null to indicate no restriction
        };
      default:
        return {
          startDate: null,
          endDate: null,
        };
    }
  };

  const listbg = useColorModeValue("blue.50", "gray.600");
  const containbg = useColorModeValue("white", "gray.700");

  return (
    <Box position="relative" ref={dropdownRef}>
      <Button
        onClick={() => setIsDropdownOpen((prev) => !prev)}
        rightIcon={
          isDropdownOpen ? (
            <FaChevronUp color="#718096" />
          ) : (
            <FaChevronDown color="#718096" />
          )
        }
        variant="outline"
        width="full"
        color="gray.800"
        fontWeight="medium"
        px={4}
        cursor="pointer"
        borderRadius="md"
        boxShadow="sm"
      >
        <Text color={"gray.500"}>{selectedOption}</Text>
      </Button>
      {isDropdownOpen && (
        <VStack
          position="absolute"
          top="100%"
          left={0}
          width="135px"
          bg={containbg}
          boxShadow="md"
          borderRadius="md"
          mt={2}
          spacing={0}
          zIndex={10}
        >
          {predefinedOptions.map((option) => (
            <Box
              key={option.value}
              width="full"
              py={2}
              px={4}
              textAlign="left"
              cursor="pointer"
              bg={selectedOption === option.label ? listbg : "transparent"}
              color={selectedOption === option.label ? "blue.500" : "inherit"}
              fontWeight={selectedOption === option.label ? "bold" : "normal"}
              _hover={{ bg: listbg }}
              onClick={() => handleOptionClick(option)}
            >
              {option.label}
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  );
};

export default DateSelector;
